import React, { FunctionComponent } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { HTMLColors } from '../../Models/Enums';
import './SentViaBadge.scss';

interface Props {
    usedDedicated: boolean;
    usedShared: boolean;
    // The campaign's chosen daily cap; 0 means no cap (send as fast as the parliament allows).
    dailySendLimit?: number | null;
    sendWindowStart?: string | null;
    sendWindowEnd?: string | null;
    sendDays?: string | null;
    // Disambiguates the popover/tooltip ids when many badges render in one grid.
    idSuffix?: string | number;
}

const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

// Stored times can arrive as HH:MM or HH:MM:SS; show HH:MM either way.
const toHourMinute = (time?: string | null): string | null => (time ? time.slice(0, 5) : null);

/**
 * The "Send via" badge shared by the scheduled and reporting campaign grids: a blue D pill whose
 * popover shows the dedicated send window and send days, and a grey S pill with a title-only
 * popover. Shows whichever legs the campaign actually used.
 */
const SentViaBadge: FunctionComponent<Props> = (props: Props) => {
    const start = toHourMinute(props.sendWindowStart);
    const end = toHourMinute(props.sendWindowEnd);
    const sendDays = props.sendDays && props.sendDays.length === 7 ? props.sendDays : null;
    const suffix = props.idSuffix ?? '';

    const pill = (letter: string, colour: string, overlay: React.ReactElement) => (
        <OverlayTrigger overlay={overlay} trigger={['hover', 'focus']}>
            <span className="pill" style={{ backgroundColor: colour, cursor: 'help' }}>
                {letter}
            </span>
        </OverlayTrigger>
    );

    const dedicatedPopover = (
        <Popover id={`sent-via-dedicated-${suffix}`} className="sent-via-popover">
            <Popover.Title>Dedicated</Popover.Title>
            <Popover.Content>
                <div>
                    {props.dailySendLimit && props.dailySendLimit > 0
                        ? `Up to ${props.dailySendLimit.toLocaleString()} emails/day`
                        : 'No daily limit'}
                </div>
                {start && end && (
                    <div>
                        Between {start} and {end}
                    </div>
                )}
                {sendDays && (
                    <div style={{ marginTop: '3px', whiteSpace: 'nowrap' }}>
                        {DAY_LABELS.map((label, index) => (
                            <span key={index} style={{ marginRight: '6px' }}>
                                {label}{' '}
                                <span
                                    style={{ color: sendDays[index] === '1' ? HTMLColors.SUCCESS : HTMLColors.DANGER }}
                                >
                                    {sendDays[index] === '1' ? '✓' : '✗'}
                                </span>
                            </span>
                        ))}
                    </div>
                )}
            </Popover.Content>
        </Popover>
    );

    const sharedPopover = (
        <Popover id={`sent-via-shared-${suffix}`} className="sent-via-popover sent-via-popover--title-only">
            <Popover.Title>Shared</Popover.Title>
        </Popover>
    );

    return (
        <span className="sent-via-badge" style={{ display: 'inline-flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
            {props.usedDedicated && pill('D', HTMLColors.PRIMARY, dedicatedPopover)}
            {props.usedShared && pill('S', HTMLColors.GREY_SCALE, sharedPopover)}
        </span>
    );
};

export default SentViaBadge;
